/**
 * The last good payload, persisted per browser.
 *
 * Why this exists: the bar paints the compiled-in FALLBACK_CONFIG before the fetch
 * resolves, and the catalog is the one field that can still legitimately differ from it —
 * `enabled` states drift from the config row by design (see fallback.ts). Because the tile
 * row is centred on the bar, that drift is expensive: every tile the fetch adds or removes
 * shifts the whole row by half a tile, so a 2-tile fallback resolving to a 7-tile config
 * moves the Home tile 230px. Persisting the last good payload makes the second and every
 * subsequent visit paint the catalog the viewer actually saw last time.
 *
 * It cannot help the FIRST visit, and it cannot help the first paint of a server-rendered
 * host: the server has no access to the browser's localStorage, so its HTML is always the
 * compiled-in fallback. What this removes is the hydration-time swap on every return
 * visit. See provider.tsx for how the read is sequenced against paint.
 *
 * EVERY access is wrapped, including the `localStorage` property read itself. Safari's
 * private mode and Chrome with site data blocked throw a SecurityError on ACCESS, not just
 * on setItem, so `typeof localStorage !== 'undefined'` is not a sufficient guard.
 */
/**
 * Versioned on the RENDERER's expected shape, not on the payload's `schema_version`. A
 * renderer that changes what it stores bumps this key rather than trying to migrate, so
 * an old blob is orphaned and ignored instead of misread. localStorage is origin-scoped
 * and a host has exactly one configUrl, so the key needs no URL in it.
 */
const STORAGE_KEY = 'ahsuite-nav-config-v1';
/** Beyond this a dormant browser revalidates rather than painting something ancient. */
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
function store() {
    try {
        return globalThis.localStorage ?? null;
    }
    catch {
        return null;
    }
}
/**
 * The stored payload, or null. Returns the RAW payload deliberately — the caller runs it
 * through `normalize` exactly as it would a network body, because a blob written by an
 * older renderer is untrusted input of precisely the same kind.
 */
function readStored() {
    try {
        const raw = store()?.getItem(STORAGE_KEY);
        if (!raw) {
            return null;
        }
        const envelope = JSON.parse(raw);
        if (typeof envelope !== 'object' || envelope === null) {
            return null;
        }
        const { saved_at: savedAt, payload } = envelope;
        if (typeof savedAt !== 'number') {
            return null;
        }
        // Absolute difference, so a clock moved BACKWARDS discards the entry rather than
        // treating a future timestamp as permanently fresh.
        if (Math.abs(Date.now() - savedAt) > MAX_AGE_MS) {
            return null;
        }
        return payload ?? null;
    }
    catch {
        return null;
    }
}
/** Best effort. A full quota or a blocked store is not a reason to fail the fetch. */
function writeStored(payload) {
    try {
        store()?.setItem(STORAGE_KEY, JSON.stringify({ saved_at: Date.now(), payload }));
    }
    catch {
        return;
    }
}
export { MAX_AGE_MS, STORAGE_KEY, readStored, writeStored };
//# sourceMappingURL=storage.js.map